import React from "react";

// Définition minimale pour un menu item (à adapter si besoin)
export interface U2000MenuItem {
  id?: string | number;
  link?: string;
  default?: boolean;
  label?: string;
  allowedRoles?: string[];
  /**
   * BL-0339 — si true, l’item n’est affiché / retenu dans auth.menu que si
   * `auth.user.organizationId` est présent (ex. Mon organisation U3148).
   */
  requiresOrganizationId?: boolean;
  showInGlobalSearch?: boolean;
  quickSearchFieldName?: string;
  quickSearchCondition?: string;
  /** Ordre d'affichage dans le Quick Search (1 = premier). Items sans ordre apparaissent après. */
  quickSearchOrder?: number;
  menuItems?: U2000MenuItem[];
  [key: string]: any;
}

export const u2000MenuGetIcon = (IconComponent: React.ElementType, className: string = ""): React.ReactElement => {
  return React.createElement(IconComponent, {
    className: `${className}`,
  });
};

export function U2000MenuFindModuleFromPath(menu: U2000MenuItem[], path: string): U2000MenuItem | null {
  let module: U2000MenuItem | null = null;
  function traverse(menu: U2000MenuItem[], path: string): U2000MenuItem | null {
    for (const item of menu) {
      if (item.menuItems) {
        return traverse(item.menuItems, path);
      } else if (path && item.link && path.includes(item.link)) {
        return item;
      }
    }
    return null;
  }

  if (menu && menu.length > 0) {
    for (const item of menu) {
      if (item.menuItems) {
        const menuItem = traverse(item.menuItems, path);
        if (menuItem) {
          module = item;
          break;
        }
      }
    }
  }

  return module;
}

export function U2000MenuFindMenuItemFromPath(menu: U2000MenuItem[], path: string): U2000MenuItem | null {
  let menuItem: U2000MenuItem | null = null;
  function traverse(menu: U2000MenuItem[], path: string): U2000MenuItem | null {
    for (const item of menu) {
      if (item.menuItems) {
        return traverse(item.menuItems, path);
      } else if (path && item.link && path.includes(item.link)) {
        return item;
      }
    }
    return null;
  }

  if (menu && menu.length > 0) {
    for (const item of menu) {
      if (item.menuItems) {
        menuItem = traverse(item.menuItems, path);
        if (menuItem) {
          break;
        }
      }
    }
  }

  return menuItem;
}

export function U2000MenuFindDefaultLUFromModule(menu: { menuItems?: U2000MenuItem[] }): U2000MenuItem | null {
  let defaultLU: U2000MenuItem | null = null;
  function traverse(menu: U2000MenuItem[]): U2000MenuItem | null {
    for (const item of menu) {
      if (item.menuItems) {
        return traverse(item.menuItems);
      } else if (item.default) {
        return item;
      }
    }
    return null;
  }

  if (menu.menuItems) {
    defaultLU = traverse(menu.menuItems);
  }

  return defaultLU;
}

export function U2000MenuFindFirsttLUFromModule(menu: { menuItems?: U2000MenuItem[] }): U2000MenuItem | null {
  let defaultLU: U2000MenuItem | null = null;
  function traverse(menu: U2000MenuItem[]): U2000MenuItem | null {
    for (const item of menu) {
      if (item.menuItems) {
        return traverse(item.menuItems);
      } else {
        return item;
      }
    }
    return null;
  }

  if (menu && menu.menuItems) {
    defaultLU = traverse(menu.menuItems);
  }

  return defaultLU;
}

export function U2000MenuFindMenuItemByIdFromModule(menu: { menuItems?: U2000MenuItem[] }, id: string | number): U2000MenuItem | null {
  let menuItem: U2000MenuItem | null = null;
  function traverse(menu: U2000MenuItem[], id: string | number): U2000MenuItem | null {
    for (const item of menu) {
      if (item.menuItems) {
        return traverse(item.menuItems, id);
      } else if (item.id === id) {
        return item;
      }
    }
    return null;
  }

  if (menu.menuItems) {
    menuItem = traverse(menu.menuItems, id);
  }

  return menuItem;
}

export function U2000MenuGetGlobalSearchItems(menu: U2000MenuItem[]): U2000MenuItem[] {
  type IndexedItem = { item: U2000MenuItem; traversalIndex: number };
  const indexed: IndexedItem[] = [];
  let traversalIndex = 0;

  const traverse = (items: U2000MenuItem[]) => {
    for (const item of items) {
      const hasSubItems = Array.isArray(item.menuItems) && item.menuItems.length > 0;
      if (hasSubItems) {
        traverse(item.menuItems!);
      }

      if (item.showInGlobalSearch === true) {
        // On privilégie les items terminaux (ou avec lien) pour un filtre de recherche.
        if (!hasSubItems || item.link) {
          indexed.push({ item, traversalIndex });
          traversalIndex += 1;
        }
      }
    }
  };

  traverse(menu ?? []);

  indexed.sort((a, b) => {
    const aOrder = a.item.quickSearchOrder;
    const bOrder = b.item.quickSearchOrder;
    const aHasOrder = typeof aOrder === "number";
    const bHasOrder = typeof bOrder === "number";

    if (aHasOrder && bHasOrder) {
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      return a.traversalIndex - b.traversalIndex;
    }
    if (aHasOrder && !bHasOrder) {
      return -1;
    }
    if (!aHasOrder && bHasOrder) {
      return 1;
    }
    return a.traversalIndex - b.traversalIndex;
  });

  return indexed.map(({ item }) => item);
}
